// DeviceFiltersPanel – sidebar controls for slicing and dicing the device list.

import React, { ChangeEvent, JSX, useMemo } from 'react';
import { DeviceFiltersValue, DEFAULT_DEVICE_FILTERS } from '../../../../types/DeviceListTypes';
import {
    CMD_REAR_FW_DWNLD,
    CMD_REAR_LOG_UPLD,
    CMD_REAR_DELETE_LOGS,
    CMD_REAR_TOGGLE_LED,
    CMD_REAR_APP_MODE,
    CMD_REAR_RACE_MODE,
    CMD_REAR_SLEEP,
    CMD_REAR_REBOOT,
    CMD_REAR_WINTER,
    CMD_REAR_MODEM_DFOTA,
    CMD_FRONT_FW_DWNLD,
    CMD_FRONT_LOG_UPLD,
    CMD_FRONT_DELETE_LOGS,
    CMD_FRONT_TOGGLE_LED,
} from '../../../../types/Commands';
import DeviceCsvFilter from './DeviceCSVFilter';

// ---- Types ----

interface DeviceFiltersPanelProps {
    value: DeviceFiltersValue;
    onChange: (next: DeviceFiltersValue) => void;
    onCsvFilterIdsChange?: (ids: string[] | null) => void;
    /** When false, lifecycle filter is hidden (non-admins cannot load lifecycle data). */
    lifecycleFilterEnabled?: boolean;
}

type CommandFilterStatus =
    | 'PENDING'
    | 'ACKED'
    | 'IN_PROGRESS'
    | 'SUCCEEDED'
    | 'FAILED'
    | 'TIMED_OUT'
    | 'CANCELLED';

// ---- Constants ----

const DEFAULT_FILTERS: DeviceFiltersValue = DEFAULT_DEVICE_FILTERS;

const STATUS_OPTIONS: { value: CommandFilterStatus; label: string }[] = [
    { value: 'PENDING', label: 'PENDING' },
    { value: 'ACKED', label: 'ACKED' },
    { value: 'IN_PROGRESS', label: 'IN_PROGRESS' },
    { value: 'SUCCEEDED', label: 'SUCCEEDED' },
    { value: 'FAILED', label: 'FAILED' },
    { value: 'TIMED_OUT', label: 'TIMED_OUT' },
    { value: 'CANCELLED', label: 'CANCELLED' },
];

// ---- Component ----

export function DeviceFiltersPanel(props: DeviceFiltersPanelProps): JSX.Element {
    const { value, onChange, onCsvFilterIdsChange, lifecycleFilterEnabled = true } = props;

    /** Generic select handler – writes string value into filter key. */
    function handleSelect<K extends keyof DeviceFiltersValue>(key: K) {
        return function onSelect(e: ChangeEvent<HTMLSelectElement>) {
            onChange({ ...value, [key]: e.target.value as any });
        };
    }

    /** Generic number input – null when empty, else parsed number. */
    function handleNumber<K extends keyof DeviceFiltersValue>(key: K) {
        return function onNum(e: ChangeEvent<HTMLInputElement>) {
            const raw = e.target.value;
            if (raw === '') {
                onChange({ ...value, [key]: null as any });
                return;
            }
            const num = Number(raw);
            onChange({
                ...value,
                [key]: Number.isNaN(num) ? (null as any) : (num as any),
            });
        };
    }

    /** Generic text input handler. */
    function handleText<K extends keyof DeviceFiltersValue>(key: K) {
        return function onTxt(e: ChangeEvent<HTMLInputElement>) {
            onChange({ ...value, [key]: e.target.value as any });
        };
    }

    /** Generic datetime-local input handler. */
    function handleDateTime<K extends keyof DeviceFiltersValue>(key: K) {
        return function onDt(e: ChangeEvent<HTMLInputElement>) {
            onChange({ ...value, [key]: e.target.value as any });
        };
    }

    /** Toggle one command status in the multi-select list. */
    function toggleStatus(status: CommandFilterStatus) {
        const current = value.lastCmdStatuses ?? [];
        const exists = current.includes(status);
        const next = exists ? current.filter((s) => s !== status) : [...current, status];
        onChange({ ...value, lastCmdStatuses: next });
    }

    /** Reset all filters to defaults and clear CSV filter. */
    function handleReset() {
        onChange({ ...DEFAULT_FILTERS });
        if (onCsvFilterIdsChange) onCsvFilterIdsChange(null);
    }

    /** How many filter fields are non-default (for badge). */
    const activeCount = useMemo(() => {
        let count = 0;

        if (value.online !== 'any') count++;
        if (value.tls !== 'any') count++;
        if (value.lifecycle !== 'any') count++;
        if (value.mode !== 'any') count++;
        if (value.deviceRole !== 'any') count++;
        if (value.frontLink !== 'any') count++;

        if (value.battMvMin != null) count++;
        if (value.battMvMax != null) count++;

        if (value.raceLogMin != null) count++;
        if (value.raceLogMax != null) count++;

        if (value.debugLogMin != null) count++;
        if (value.debugLogMax != null) count++;

        if (value.lastHeardFrom) count++;
        if (value.lastHeardTo) count++;

        if (value.lastCmdType !== 'any') count++;
        if (value.lastCmdStatuses && value.lastCmdStatuses.length > 0) count++;
        if ((value.lastCmdTargetContains ?? '').trim().length > 0) count++;
        if (value.lastCmdUpdatedFrom) count++;
        if (value.lastCmdUpdatedTo) count++;
        if (value.locationCenterLat != null) count++;
        if (value.locationCenterLng != null) count++;
        if (value.locationRadiusKm != null) count++;

        return count;
    }, [value]);

    return (
        <div className="h-full min-h-0 flex flex-col text-xs">
            {/* Header with active count and reset */}
            <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2 min-w-0">
                    <span className="font-semibold">Filters</span>
                    {activeCount > 0 && (
                        <span className="px-1.5 py-0.5 rounded-full bg-blue-100 text-blue-800 text-[10px] dark:bg-blue-950/60 dark:text-blue-200 dark:bg-blue-950/60 dark:text-blue-200 dark:bg-blue-950/60 dark:text-blue-200">
                            {activeCount} active
                        </span>
                    )}
                </div>

                <button
                    type="button"
                    onClick={handleReset}
                    className="px-2 py-1 rounded border border-gray-300 text-[11px] hover:bg-gray-50 dark:border-slate-600 dark:hover:bg-slate-800 dark:text-slate-200"
                >
                    Reset
                </button>
            </div>

            {/* Scrollable filter sections: device status, battery, logs, last heard, last command */}
            <div className="flex-1 min-h-0 overflow-y-auto pr-1 space-y-4">
                <div>
                    <div className="text-[11px] font-semibold text-gray-700 dark:text-slate-300 mb-1">Device status</div>
                    <div className="space-y-2">
                        <select
                            className="w-full px-2 py-1 rounded border border-gray-300 bg-white text-[11px] dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100"
                            value={value.online}
                            onChange={handleSelect('online')}
                        >
                            <option value="any">Any status</option>
                            <option value="online">Online</option>
                            <option value="offline">Offline</option>
                        </select>

                        <select
                            className="w-full px-2 py-1 rounded border border-gray-300 bg-white text-[11px] dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100"
                            value={value.tls}
                            onChange={handleSelect('tls')}
                        >
                            <option value="any">Any TLS</option>
                            <option value="provisioned">TLS provisioned</option>
                            <option value="unprovisioned">TLS unprovisioned</option>
                        </select>

                        {lifecycleFilterEnabled && (
                            <select
                                className="w-full px-2 py-1 rounded border border-gray-300 bg-white text-[11px] dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100"
                                value={value.lifecycle}
                                onChange={handleSelect('lifecycle')}
                            >
                                <option value="any">Any lifecycle</option>
                                <option value="active">In service (active)</option>
                                <option value="lost">Lost</option>
                                <option value="decommissioned">Decommissioned</option>
                                <option value="damaged">Damaged</option>
                                <option value="errored">Errored</option>
                            </select>
                        )}

                        <select
                            className="w-full px-2 py-1 rounded border border-gray-300 bg-white text-[11px] dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100"
                            value={value.mode}
                            onChange={handleSelect('mode')}
                        >
                            <option value="any">Any mode</option>
                            <option value="app">App</option>
                            <option value="race">Race</option>
                            <option value="charging">Charging</option>
                            <option value="busy">Busy</option>
                            <option value="offline">Offline</option>
                        </select>

                        <select
                            className="w-full px-2 py-1 rounded border border-gray-300 bg-white text-[11px] dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100"
                            value={value.deviceRole}
                            onChange={handleSelect('deviceRole')}
                        >
                            <option value="any">Any role</option>
                            <option value="rear">Rear only</option>
                            <option value="front">Front only</option>
                        </select>

                        <select
                            className="w-full px-2 py-1 rounded border border-gray-300 bg-white text-[11px] dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100"
                            value={value.frontLink}
                            onChange={handleSelect('frontLink')}
                        >
                            <option value="any">Any front link</option>
                            <option value="linked">Front linked</option>
                            <option value="unlinked">Front unlinked</option>
                        </select>
                    </div>
                </div>

                <div>
                    <div className="text-[11px] font-semibold text-gray-700 dark:text-slate-300 mb-1">Battery (mV)</div>
                    <div className="grid grid-cols-2 gap-2">
                        <input
                            type="number"
                            className="w-full px-2 py-1 rounded border border-gray-300 bg-white text-[11px] dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100"
                            placeholder="Min"
                            value={value.battMvMin ?? ''}
                            onChange={handleNumber('battMvMin')}
                        />
                        <input
                            type="number"
                            className="w-full px-2 py-1 rounded border border-gray-300 bg-white text-[11px] dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100"
                            placeholder="Max"
                            value={value.battMvMax ?? ''}
                            onChange={handleNumber('battMvMax')}
                        />
                    </div>
                </div>

                <div>
                    <div className="text-[11px] font-semibold text-gray-700 dark:text-slate-300 mb-1">Race log size (B)</div>
                    <div className="grid grid-cols-2 gap-2">
                        <input
                            type="number"
                            className="w-full px-2 py-1 rounded border border-gray-300 bg-white text-[11px] dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100"
                            placeholder="Min"
                            value={value.raceLogMin ?? ''}
                            onChange={handleNumber('raceLogMin')}
                        />
                        <input
                            type="number"
                            className="w-full px-2 py-1 rounded border border-gray-300 bg-white text-[11px] dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100"
                            placeholder="Max"
                            value={value.raceLogMax ?? ''}
                            onChange={handleNumber('raceLogMax')}
                        />
                    </div>
                </div>

                <div>
                    <div className="text-[11px] font-semibold text-gray-700 dark:text-slate-300 mb-1">Debug log size (B)</div>
                    <div className="grid grid-cols-2 gap-2">
                        <input
                            type="number"
                            className="w-full px-2 py-1 rounded border border-gray-300 bg-white text-[11px] dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100"
                            placeholder="Min"
                            value={value.debugLogMin ?? ''}
                            onChange={handleNumber('debugLogMin')}
                        />
                        <input
                            type="number"
                            className="w-full px-2 py-1 rounded border border-gray-300 bg-white text-[11px] dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100"
                            placeholder="Max"
                            value={value.debugLogMax ?? ''}
                            onChange={handleNumber('debugLogMax')}
                        />
                    </div>
                </div>

                <div>
                    <div className="text-[11px] font-semibold text-gray-700 dark:text-slate-300 mb-1">Last heard</div>
                    <div className="space-y-2">
                        <input
                            type="datetime-local"
                            className="w-full px-2 py-1 rounded border border-gray-300 bg-white text-[11px] dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100"
                            value={value.lastHeardFrom}
                            onChange={handleDateTime('lastHeardFrom')}
                        />
                        <input
                            type="datetime-local"
                            className="w-full px-2 py-1 rounded border border-gray-300 bg-white text-[11px] dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100"
                            value={value.lastHeardTo}
                            onChange={handleDateTime('lastHeardTo')}
                        />
                    </div>
                </div>

                <div>
                    <div className="text-[11px] font-semibold text-gray-700 dark:text-slate-300 mb-1">Last command</div>
                    <div className="space-y-2">
                        <select
                            className="w-full px-2 py-1 rounded border border-gray-300 bg-white text-[11px] dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100"
                            value={value.lastCmdType}
                            onChange={handleSelect('lastCmdType')}
                        >
                            <option value="any">Any type</option>
                            <option value={CMD_REAR_FW_DWNLD}>Firmware download (rear)</option>
                            <option value={CMD_FRONT_FW_DWNLD}>Firmware download (front)</option>
                            <option value={CMD_REAR_LOG_UPLD}>Log upload (rear)</option>
                            <option value={CMD_FRONT_LOG_UPLD}>Log upload (front)</option>
                            <option value={CMD_REAR_DELETE_LOGS}>Delete logs (rear)</option>
                            <option value={CMD_FRONT_DELETE_LOGS}>Delete logs (front)</option>
                            <option value={CMD_REAR_TOGGLE_LED}>Toggle LED (rear)</option>
                            <option value={CMD_FRONT_TOGGLE_LED}>Toggle LED (front)</option>
                            <option value={CMD_REAR_APP_MODE}>App mode</option>
                            <option value={CMD_REAR_RACE_MODE}>Race mode</option>
                            <option value={CMD_REAR_SLEEP}>Sleep</option>
                            <option value={CMD_REAR_REBOOT}>Reboot</option>
                            <option value={CMD_REAR_WINTER}>Winter mode</option>
                            <option value={CMD_REAR_MODEM_DFOTA}>Modem DFOTA</option>
                        </select>


                        <input
                            type="text"
                            className="w-full px-2 py-1 rounded border border-gray-300 bg-white text-[11px] dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100"
                            placeholder="Target version contains…"
                            value={value.lastCmdTargetContains}
                            onChange={handleText('lastCmdTargetContains')}
                        />

                        <div className="text-[10px] text-gray-500 dark:text-slate-400">Status (multi-select)</div>
                        <div className="flex flex-wrap gap-1">
                            {STATUS_OPTIONS.map((opt) => {
                                const active = value.lastCmdStatuses?.includes(opt.value) ?? false;
                                return (
                                    <button
                                        key={opt.value}
                                        type="button"
                                        onClick={() => toggleStatus(opt.value)}
                                        className={
                                            active
                                                ? 'px-2 py-1 rounded-full border text-[10px] bg-blue-100 border-blue-400 text-blue-800 dark:bg-blue-950/60 dark:border-blue-500 dark:text-blue-200'
                                                : 'px-2 py-1 rounded-full border text-[10px] bg-gray-50 border-gray-300 text-gray-700 hover:bg-gray-100 dark:bg-slate-800 dark:border-slate-600 dark:text-slate-200 dark:hover:bg-slate-700'
                                        }
                                    >
                                        {opt.label}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                </div>

                <div>
                    <div className="text-[11px] font-semibold text-gray-700 dark:text-slate-300 mb-1">Last command updated</div>
                    <div className="space-y-2">
                        <input
                            type="datetime-local"
                            className="w-full px-2 py-1 rounded border border-gray-300 bg-white text-[11px] dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100"
                            value={value.lastCmdUpdatedFrom}
                            onChange={handleDateTime('lastCmdUpdatedFrom')}
                        />
                        <input
                            type="datetime-local"
                            className="w-full px-2 py-1 rounded border border-gray-300 bg-white text-[11px] dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100"
                            value={value.lastCmdUpdatedTo}
                            onChange={handleDateTime('lastCmdUpdatedTo')}
                        />
                    </div>
                </div>

                <div>
                    <div className="text-[11px] font-semibold text-gray-700 dark:text-slate-300 mb-1">Location radius</div>
                    <div className="grid grid-cols-3 gap-2">
                        <input
                            type="number"
                            className="w-full px-2 py-1 rounded border border-gray-300 bg-white text-[11px] dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100"
                            placeholder="Lat"
                            value={value.locationCenterLat ?? ''}
                            onChange={handleNumber('locationCenterLat')}
                        />
                        <input
                            type="number"
                            className="w-full px-2 py-1 rounded border border-gray-300 bg-white text-[11px] dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100"
                            placeholder="Lng"
                            value={value.locationCenterLng ?? ''}
                            onChange={handleNumber('locationCenterLng')}
                        />
                        <input
                            type="number"
                            min={0}
                            className="w-full px-2 py-1 rounded border border-gray-300 bg-white text-[11px] dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100"
                            placeholder="km"
                            value={value.locationRadiusKm ?? ''}
                            onChange={handleNumber('locationRadiusKm')}
                        />
                    </div>
                    <div className="mt-1 text-[10px] text-gray-500 dark:text-slate-400">
                        Show devices within radius of the center point.
                    </div>
                </div>

                {onCsvFilterIdsChange && (
                    <div>
                        <div className="text-[11px] font-semibold text-gray-700 dark:text-slate-300 mb-1">CSV device filter</div>
                        <div className="text-[10px] text-gray-500 dark:text-slate-400 mb-2">Upload a list of device IDs</div>
                        <DeviceCsvFilter onFilterIdsChange={onCsvFilterIdsChange} />
                    </div>
                )}
            </div>
        </div>
    );
}